import React, { useState, useMemo, useEffect } from 'react';
import { formatProjectDateRange } from '../utils/dateUtils';

interface AvailabilityCalendarProps {
  selectedDates: string[];
  onDatesChange: (dates: string[]) => void;
}

const getDatesInRange = (startStr: string, endStr: string): string[] => {
    const dates = [];
    // Parse dates in local timezone to avoid timezone issues
    const startDate = new Date(startStr + 'T00:00:00');
    const endDate = new Date(endStr + 'T00:00:00');
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        // Use consistent YYYY-MM-DD format without timezone conversion
        const dateString = currentDate.getFullYear() + '-' + 
          String(currentDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(currentDate.getDate()).padStart(2, '0');
        dates.push(dateString);
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
};

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({ selectedDates, onDatesChange }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectionStart, setSelectionStart] = useState<string | null>(null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  // Reset selection state only when hoveredDate needs clearing
  useEffect(() => {
    console.log('📅 [AvailabilityCalendar] selectedDates changed:', selectedDates);
    // Only reset hover state, keep selection state for persistent selection
    setHoveredDate(null);
    // Only reset selectionStart if we have completed dates (to avoid interrupting active selection)
    if (selectedDates.length > 1) {
      setSelectionStart(null);
    }
  }, [selectedDates]);

  const handleDateClick = (date: Date) => {
    // Ensure we get YYYY-MM-DD format, not ISO string with timezone
    const dateString = date.getFullYear() + '-' + 
      String(date.getMonth() + 1).padStart(2, '0') + '-' + 
      String(date.getDate()).padStart(2, '0');

    console.log('📅 [AvailabilityCalendar] Date clicked:', dateString, 'selectionStart:', selectionStart);

    // If clicking on already selected date, toggle it off (unless it's part of a range)
    if (selectedDates.includes(dateString)) {
      if (selectedDates.length === 1) {
        // Single date selected, clear it
        console.log('📅 [AvailabilityCalendar] Removing single selected date');
        onDatesChange([]);
        setSelectionStart(null);
        return;
      } else if (selectedDates.length > 1) {
        // Multiple dates selected, start new selection from this date
        console.log('📅 [AvailabilityCalendar] Starting new selection from selected date');
        onDatesChange([dateString]);
        setSelectionStart(dateString);
        return;
      }
    }

    if (selectionStart) {
      // This is the second click (end of range)
      const start = new Date(selectionStart + 'T00:00:00');
      const end = new Date(dateString + 'T00:00:00');
      const range = start < end 
        ? getDatesInRange(selectionStart, dateString)
        : getDatesInRange(dateString, selectionStart);
      
      console.log('📅 [AvailabilityCalendar] Selected date range:', range);
      onDatesChange(range);
      setSelectionStart(null);
    } else {
      // First click (start of a new selection)
      console.log('📅 [AvailabilityCalendar] Starting new selection:', dateString);
      onDatesChange([dateString]);
      setSelectionStart(dateString);
    }
  };

  const changeMonth = (offset: number) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + offset);
      return newDate;
    });
  };

  const clearSelection = () => {
    console.log('📅 [AvailabilityCalendar] Clearing selection');
    onDatesChange([]);
    setSelectionStart(null);
    setHoveredDate(null);
  };

  const renderCalendar = (dateToRender: Date) => {
    const year = dateToRender.getFullYear();
    const month = dateToRender.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const hoverRange = useMemo(() => {
        if (!selectionStart || !hoveredDate) return [];
        const start = new Date(selectionStart);
        const end = new Date(hoveredDate);
        return start < end
            ? getDatesInRange(selectionStart, hoveredDate)
            : getDatesInRange(hoveredDate, selectionStart);
    }, [selectionStart, hoveredDate]);

    const days = Array.from({ length: startDayOfWeek }, (_, i) => (
      <div key={`empty-start-${i}`} className="w-10 h-10"></div>
    ));

    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      // Ensure consistent YYYY-MM-DD format
      const dayString = dayDate.getFullYear() + '-' + 
        String(dayDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(dayDate.getDate()).padStart(2, '0');
      const isSelected = selectedDates.includes(dayString);
      const isPast = dayDate < new Date(new Date().toDateString());
      
      // Sort selected dates to properly identify start and end
      const sortedSelectedDates = [...selectedDates].sort();
      const isRangeStart = isSelected && dayString === sortedSelectedDates[0];
      const isRangeEnd = isSelected && dayString === sortedSelectedDates[sortedSelectedDates.length - 1];
      const isInRange = isSelected && !isRangeStart && !isRangeEnd && sortedSelectedDates.length > 2;
      const isInHoverRange = hoverRange.includes(dayString) && !isSelected;

      const buttonClasses = [
        'w-10 h-10 flex items-center justify-center text-sm transition-all duration-200 rounded-full',
        isPast ? 'text-gray-600 cursor-not-allowed' : 'hover:bg-gray-600 hover:scale-105',
        isSelected ? 'text-white font-bold bg-indigo-600 shadow-lg' : 'text-gray-200',
        (isRangeStart || isRangeEnd) ? 'ring-2 ring-indigo-300 ring-offset-1 ring-offset-gray-700' : '',
        isInRange && !isRangeStart && !isRangeEnd ? 'bg-indigo-700 text-indigo-100' : '',
        isInHoverRange ? 'bg-gray-600 bg-opacity-50 scale-105' : '',
      ].filter(Boolean).join(' ');

      days.push(
        <button
          type="button"
          key={i}
          onClick={() => !isPast && handleDateClick(dayDate)}
          onMouseEnter={() => !isPast && setHoveredDate(dayString)}
          disabled={isPast}
          className={buttonClasses}
        >
          {i}
        </button>
      );
    }
    
    return (
        <div className="bg-gray-700 p-3 rounded-md">
            <div className="flex justify-between items-center mb-3">
                <button type="button" onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-gray-600">&lt;</button>
                <span className="font-semibold text-white">{dateToRender.toLocaleString('ru-RU', { month: 'long', year: 'numeric' })}</span>
                <button type="button" onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-gray-600">&gt;</button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-400 mb-2">
                <div>Пн</div><div>Вт</div><div>Ср</div><div>Чт</div><div>Пт</div><div>Сб</div><div>Вс</div>
            </div>
            <div className="grid grid-cols-7 gap-1" onMouseLeave={() => setHoveredDate(null)}>
                {days}
            </div>
        </div>
    );
  }

  const nextMonthDate = new Date(currentDate);
  nextMonthDate.setMonth(currentDate.getMonth() + 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderCalendar(currentDate)}
        {renderCalendar(nextMonthDate)}
        {selectedDates.length > 0 && (
          <div className="md:col-span-2 bg-gray-800 rounded-md p-3 mt-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-indigo-300">
                  📅 Доступность: {selectedDates.length} дней
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {selectedDates.length === 1 
                    ? formatProjectDateRange(selectedDates[0], selectedDates[0])
                    : formatProjectDateRange(selectedDates[0], selectedDates[selectedDates.length - 1])
                  }
                </p>
              </div>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-gray-400 hover:text-red-400 px-2 py-1 rounded transition-colors"
                title="Очистить выбор"
              >
                ✕ Очистить
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default AvailabilityCalendar;